// New Evaluation Page - Create evaluations with 15 criteria
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// Strip accents from uppercase Greek letters (e.g., Ά→Α, Έ→Ε, Ό→Ο)
const stripGreekAccents = (text) => {
  const map = { 'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω', 'ΐ': 'ϊ', 'ΰ': 'ϋ' };
  return text.replace(/[ΆΈΉΊΌΎΏ]/g, ch => map[ch] || ch);
};
import Layout from '../components/Layout';
import { evaluationsApi, eventsApi, usersApi, settingsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check,
  Star
} from 'lucide-react';

// Evaluation categories with criteria
const EVALUATION_CATEGORIES = {
  communication_energy: {
    en: "Communication & Energy Management",
    el: "Επικοινωνία & Διαχείριση Ενέργειας",
    criteria: [
      { key: "clear_instructions", en: "Clear instruction communication", el: "Σαφής επικοινωνία οδηγιών" },
      { key: "positive_energy", en: "Positive energy, smile, enthusiasm", el: "Θετική ενέργεια, χαμόγελο, ενθουσιασμός" },
      { key: "time_management", en: "Management of time and activity flow", el: "Διαχείριση χρόνου και ροής δραστηριότητας" }
    ]
  },
  team_handling_safety: {
    en: "Team Handling & Safety",
    el: "Διαχείριση Ομάδας & Ασφάλεια",
    criteria: [
      { key: "team_engagement", en: "Team engagement/activation", el: "Εμπλοκή/Ενεργοποίηση ομάδας" },
      { key: "participant_safety", en: "Participant safety (physical & psychological)", el: "Ασφάλεια συμμετεχόντων (σωματική & ψυχολογική)" },
      { key: "crisis_management", en: "Management of difficult behaviors/crises", el: "Διαχείριση δύσκολων συμπεριφορών/κρίσεων" }
    ]
  },
  professionalism_reliability: {
    en: "Professionalism & Reliability",
    el: "Επαγγελματισμός & Αξιοπιστία",
    criteria: [
      { key: "timely_presence", en: "Timely presence - adherence to obligations", el: "Έγκαιρη παρουσία - τήρηση υποχρεώσεων" },
      { key: "appearance", en: "Appearance and adherence to dress code", el: "Εμφάνιση και τήρηση κώδικα ντυσίματος" },
      { key: "collaboration", en: "Collaboration with Coordinator & Operations", el: "Συνεργασία με Συντονιστή & Λειτουργίες" }
    ]
  },
  operational_execution: {
    en: "Operational Execution",
    el: "Λειτουργική Εκτέλεση",
    criteria: [
      { key: "setup_equipment", en: "Setup & equipment - correct setup/tidiness", el: "Εγκατάσταση & εξοπλισμός - σωστή τοποθέτηση/τάξη" },
      { key: "procedure_adherence", en: "Adherence to procedures", el: "Τήρηση διαδικασιών" },
      { key: "equipment_management", en: "Professional equipment management", el: "Επαγγελματική διαχείριση εξοπλισμού" }
    ]
  },
  facilitation_skills: {
    en: "GrowPlay® Facilitation Skills",
    el: "Δεξιότητες Facilitation GrowPlay®",
    criteria: [
      { key: "educational_goals", en: "Guiding team towards educational goals", el: "Καθοδήγηση ομάδας προς εκπαιδευτικούς στόχους" },
      { key: "theory_practice", en: "Connecting theory with practice", el: "Σύνδεση θεωρίας με πράξη" },
      { key: "debriefing_skills", en: "Debriefing skills / reflective conversation", el: "Δεξιότητες debriefing / αναστοχαστική συζήτηση" }
    ]
  }
};

const MICRO_TRAININGS = [
  { key: "clear_instructions_framing", en: "Clear Instructions & Framing", el: "Clear Instructions & Framing" },
  { key: "energy_management", en: "Energy Management & Group Activation", el: "Energy Management & Group Activation" },
  { key: "managing_difficult_participants", en: "Managing Difficult Participants", el: "Managing Difficult Participants" },
  { key: "emotional_regulation", en: "Emotional Regulation in Events", el: "Emotional Regulation in Events" },
  { key: "psychological_safety", en: "Psychological Safety in Teambuilding", el: "Psychological Safety in Teambuilding" },
  { key: "client_communication", en: "Client Communication On-Site", el: "Client Communication On-Site" },
  { key: "activity_setup_speed", en: "Activity Setup Speed & Precision", el: "Activity Setup Speed & Precision" },
  { key: "time_flow_control", en: "Time & Flow Control During Activities", el: "Time & Flow Control During Activities" },
  { key: "venue_adaptation", en: "Venue Adaptation & Spatial Awareness", el: "Venue Adaptation & Spatial Awareness" },
  { key: "material_management", en: "Material Management & Logistics", el: "Material Management & Logistics" },
  { key: "safety_protocols", en: "Safety Protocols in Outdoor / Large Groups", el: "Safety Protocols in Outdoor / Large Groups" },
  { key: "coordination_cofacilitator", en: "Coordination with Co-Facilitator", el: "Coordination with Co-Facilitator" },
  { key: "linking_business_outcomes", en: "Linking Activity to Business Outcomes", el: "Linking Activity to Business Outcomes" },
  { key: "strong_debriefing", en: "Strong Debriefing Techniques", el: "Strong Debriefing Techniques" }
];

export default function NewEvaluationPage() {
  const { user, isAdmin, isSeniorFacilitator } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(0);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [peerEvalEnabled, setPeerEvalEnabled] = useState(false);
  
  const [formData, setFormData] = useState({
    event_id: searchParams.get('event') || '',
    evaluatee_id: '',
    evaluation_type: 'self',
    scores: {},
    comments: {},
    what_went_well: '',
    what_to_improve: '',
    micro_trainings_requested: []
  });

  const categories = Object.keys(EVALUATION_CATEGORIES);
  const totalSteps = categories.length + 2; // Setup + categories + Review

  useEffect(() => {
    loadData();
  }, []);

  // Scroll to top after each step change
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [step]);

  const loadData = async () => {
    try {
      const [eventsRes, usersRes, peerRes] = await Promise.all([
        eventsApi.getAll(),
        usersApi.getAll(),
        settingsApi.getPeerEvaluation()
      ]);
      setEvents(eventsRes.data);
      setUsers(usersRes.data);
      setPeerEvalEnabled(peerRes.data.enabled);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (criterionKey, score) => {
    setFormData(prev => ({
      ...prev,
      scores: { ...prev.scores, [criterionKey]: score }
    }));
  };

  const handleCommentChange = (criterionKey, comment) => {
    setFormData(prev => ({
      ...prev,
      comments: { ...prev.comments, [criterionKey]: comment }
    }));
  };

  const handleTrainingToggle = (trainingKey) => {
    setFormData(prev => ({
      ...prev,
      micro_trainings_requested: prev.micro_trainings_requested.includes(trainingKey)
        ? prev.micro_trainings_requested.filter(k => k !== trainingKey)
        : [...prev.micro_trainings_requested, trainingKey]
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Format scores for API
      const scoresArray = Object.entries(formData.scores).map(([key, score]) => ({
        criterion_key: key,
        score: score
      }));
      
      await evaluationsApi.create({
        event_id: formData.event_id,
        evaluatee_id: formData.evaluation_type === 'self' ? user.user_id : formData.evaluatee_id,
        evaluation_type: formData.evaluation_type,
        scores: scoresArray,
        comments: formData.comments,
        what_went_well: formData.what_went_well,
        what_to_improve: formData.what_to_improve,
        micro_trainings_requested: formData.micro_trainings_requested
      });
      
      toast.success(language === 'el' ? 'Αξιολόγηση υποβλήθηκε!' : 'Evaluation submitted!');
      navigate('/evaluations');
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const OPTIONAL_CATEGORIES = ['facilitation_skills'];
  
  const canProceed = () => {
    if (step === 0) {
      if (!formData.event_id) return false;
      if (formData.evaluation_type !== 'self' && !formData.evaluatee_id) return false;
      return true;
    }
    if (step > 0 && step <= categories.length) {
      const categoryKey = categories[step - 1];
      // Optional categories can be skipped
      if (OPTIONAL_CATEGORIES.includes(categoryKey)) return true;
      const category = EVALUATION_CATEGORIES[categoryKey];
      return category.criteria.every(c => formData.scores[c.key] !== undefined);
    }
    // Final review step is always passable
    return true;
  };

  // Minimum required: all criteria from required (non-optional) categories
  const requiredCriteria = categories
    .filter(cat => !OPTIONAL_CATEGORIES.includes(cat))
    .flatMap(cat => EVALUATION_CATEGORIES[cat].criteria);
  const minRequiredScores = requiredCriteria.length;

  const getProgress = () => {
    const allCriteria = categories.flatMap(cat => EVALUATION_CATEGORIES[cat].criteria);
    const scoredCount = allCriteria.filter(c => formData.scores[c.key]).length;
    return (scoredCount / allCriteria.length) * 100;
  };

  const facilitators = users.filter(u => u.role === 'facilitator' || u.role === 'senior_facilitator');
  // Supervisor eval: facilitators evaluate seniors, seniors evaluate facilitators
  // Peer eval: evaluate users of the same role
  const evaluateeOptions = formData.evaluation_type === 'self' 
    ? [] 
    : formData.evaluation_type === 'supervisor'
      ? (user?.role === 'facilitator' 
          ? users.filter(u => u.role === 'senior_facilitator' && !u.is_archived)
          : users.filter(u => u.role === 'facilitator' && !u.is_archived))
      : users.filter(u => u.user_id !== user?.user_id && u.role !== 'admin' && !u.is_archived);

  const evaluateeLabel = formData.evaluation_type === 'supervisor'
    ? (user?.role === 'facilitator'
        ? (language === 'el' ? 'Επιλογή Senior Facilitator' : 'Select Senior Facilitator')
        : t('selectFacilitator'))
    : t('selectFacilitator');

  const ScoreButton = ({ score, selected, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`
        w-12 h-12 rounded-xl font-bold text-lg transition-all
        ${selected 
          ? 'bg-[#E6007E] text-white shadow-lg scale-110' 
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }
      `}
      data-testid={`score-btn-${score}`}
    >
      {score}
    </button>
  );

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 pb-20 animate-fade-in-up overflow-x-hidden" data-testid="new-evaluation-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#2A1514]">{t('newEvaluation')}</h1>
            <p className="text-gray-500 text-sm">
              {language === 'el' ? `Βήμα ${step + 1} από ${totalSteps}` : `Step ${step + 1} of ${totalSteps}`}
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <Progress value={getProgress()} className="h-2" />
          <p className="text-xs text-gray-500 text-right">{Math.round(getProgress())}% {language === 'el' ? 'ολοκληρώθηκε' : 'complete'}</p>
        </div>

        {/* Step Content */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            {/* Step 0: Setup */}
            {step === 0 && (
              <div className="space-y-6">
                <div>
                  <CardTitle className="font-heading mb-2">
                    {language === 'el' ? 'Ρύθμιση Αξιολόγησης' : 'Evaluation Setup'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'el' 
                      ? 'Επιλέξτε το event και τον τύπο αξιολόγησης'
                      : 'Select the event and evaluation type'
                    }
                  </CardDescription>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('selectEvent')}</Label>
                    <Select 
                      value={formData.event_id} 
                      onValueChange={(v) => setFormData({...formData, event_id: v})}
                    >
                      <SelectTrigger data-testid="event-select">
                        <SelectValue placeholder={language === 'el' ? 'Επιλέξτε event...' : 'Select event...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {events.map(e => (
                          <SelectItem key={e.event_id} value={e.event_id}>{e.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('evaluationType')}</Label>
                    <Select 
                      value={formData.evaluation_type} 
                      onValueChange={(v) => setFormData({...formData, evaluation_type: v, evaluatee_id: ''})}
                    >
                      <SelectTrigger data-testid="eval-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self">{t('selfEvaluation')}</SelectItem>
                        {!user?.is_probation && (
                          <SelectItem value="supervisor">
                            {user?.role === 'senior_facilitator' ? t('facilitatorEvaluation') : t('supervisorEvaluation')}
                          </SelectItem>
                        )}
                        {!user?.is_probation && (isAdmin || peerEvalEnabled) && (
                          <SelectItem value="peer">{t('peerEvaluation')}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.evaluation_type !== 'self' && (
                  <div className="space-y-2">
                    <Label>{evaluateeLabel}</Label>
                      <Select 
                        value={formData.evaluatee_id || undefined} 
                        onValueChange={(v) => setFormData({...formData, evaluatee_id: v})}
                      >
                        <SelectTrigger data-testid="evaluatee-select">
                          <SelectValue placeholder={language === 'el' ? 'Επιλέξτε...' : 'Select...'} />
                        </SelectTrigger>
                        <SelectContent>
                          {evaluateeOptions.length > 0 ? (
                            evaluateeOptions.map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                            ))
                          ) : (
                            <SelectItem value="_none" disabled>
                              {language === 'el' ? 'Δεν υπάρχουν διαθέσιμοι' : 'None available'}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Steps 1-5: Categories */}
            {step > 0 && step <= categories.length && (
              <div className="space-y-6">
                {(() => {
                  const categoryKey = categories[step - 1];
                  const category = EVALUATION_CATEGORIES[categoryKey];
                  return (
                    <>
                      <div className="border-b-2 border-[#E6007E] pb-4 mb-6">
                        <div className="flex items-center gap-3">
                          <CardTitle className="font-heading text-xl text-[#E6007E] uppercase tracking-wide">
                            {stripGreekAccents((language === 'el' ? category.el : category.en).toUpperCase())}
                          </CardTitle>
                          {OPTIONAL_CATEGORIES.includes(categoryKey) && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-normal normal-case" data-testid="optional-badge">
                              {language === 'el' ? 'Προαιρετικό' : 'Optional'}
                            </span>
                          )}
                        </div>
                        <CardDescription className="text-sm mt-2">
                          {OPTIONAL_CATEGORIES.includes(categoryKey)
                            ? (language === 'el' ? 'Μπορείτε να παραλείψετε αυτή την κατηγορία' : 'You may skip this category')
                            : (language === 'el' ? 'Βαθμολογήστε κάθε κριτήριο από 1-5' : 'Rate each criterion from 1-5')
                          }
                        </CardDescription>
                      </div>

                      <div className="space-y-6">
                        {category.criteria.map((criterion, idx) => (
                          <div key={criterion.key} className="space-y-3 p-4 bg-gray-50 rounded-lg">
                            <Label className="text-base font-normal text-gray-700">
                              {idx + 1}. {language === 'el' ? criterion.el : criterion.en}
                            </Label>
                            
                            <div className="space-y-1">
                              <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map(score => (
                                  <ScoreButton
                                    key={score}
                                    score={score}
                                    selected={formData.scores[criterion.key] === score}
                                    onClick={() => handleScoreChange(criterion.key, score)}
                                  />
                                ))}
                              </div>
                              <div className="flex justify-between text-[10px] text-gray-400 px-1">
                                <span>{language === 'el' ? 'Χρειάζεται βελτίωση' : 'Needs improvement'}</span>
                                <span>{language === 'el' ? 'Αριστο' : 'Excellent'}</span>
                              </div>
                            </div>
                            
                            <Textarea
                              placeholder={language === 'el' ? 'Σχόλιο (προαιρετικό)...' : 'Comment (optional)...'}
                              value={formData.comments[criterion.key] || ''}
                              onChange={(e) => handleCommentChange(criterion.key, e.target.value)}
                              rows={2}
                              className="text-sm"
                              data-testid={`comment-${criterion.key}`}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Final Step: Review & Trainings */}
            {step === totalSteps - 1 && (
              <div className="space-y-6">
                <div className="border-b-2 border-[#E6007E] pb-4 mb-6">
                  <CardTitle className="font-heading text-xl text-[#E6007E] uppercase tracking-wide mb-2">
                    {stripGreekAccents((language === 'el' ? 'Ολοκληρωση Αξιολογησης' : 'Complete Evaluation').toUpperCase())}
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-normal normal-case ml-3">
                      {language === 'el' ? 'Προαιρετικό' : 'Optional'}
                    </span>
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {language === 'el' 
                      ? 'Αναστοχασμός και επιλογή εκπαιδεύσεων'
                      : 'Reflection and training selection'
                    }
                  </CardDescription>
                </div>

                <div className="space-y-6">
                  {/* What went well */}
                  <div className="space-y-2 p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-green-800 font-semibold text-base">
                      {language === 'el' ? 'Κάτι που έκανα καλά σήμερα!' : 'One thing I did well today!'}
                    </Label>
                    <Textarea
                      value={formData.what_went_well}
                      onChange={(e) => setFormData({...formData, what_went_well: e.target.value})}
                      rows={3}
                      placeholder={language === 'el' ? 'Περιγράψτε τι πήγε καλά...' : 'Describe what went well...'}
                      className="bg-white"
                      data-testid="what-went-well"
                    />
                  </div>

                  {/* What to improve */}
                  <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-blue-800 font-semibold text-base">
                      {language === 'el' ? 'Τι θα βελτιώσω στο επόμενο event/workshop;' : 'What will I improve in the next event/workshop?'}
                    </Label>
                    <Textarea
                      value={formData.what_to_improve}
                      onChange={(e) => setFormData({...formData, what_to_improve: e.target.value})}
                      rows={3}
                      placeholder={language === 'el' ? 'Περιγράψτε τι θα βελτιώσετε...' : 'Describe what you will improve...'}
                      className="bg-white"
                      data-testid="what-to-improve"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="font-semibold">{t('microTrainingsRequested')}</Label>
                    <p className="text-sm text-gray-500">{t('selectTrainings')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {MICRO_TRAININGS.map(training => (
                        <div 
                          key={training.key}
                          className={`
                            flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all
                            ${formData.micro_trainings_requested.includes(training.key)
                              ? 'border-[#E6007E] bg-[#E6007E]/5'
                              : 'border-gray-200 hover:border-gray-300'
                            }
                          `}
                          onClick={() => handleTrainingToggle(training.key)}
                          data-testid={`training-${training.key}`}
                        >
                          <Checkbox 
                            checked={formData.micro_trainings_requested.includes(training.key)}
                            onCheckedChange={() => handleTrainingToggle(training.key)}
                          />
                          <span className="text-sm">
                            {language === 'el' ? training.el : training.en}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <h4 className="font-medium mb-2">
                      {language === 'el' ? 'Περίληψη' : 'Summary'}
                    </h4>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>{language === 'el' ? 'Κριτήρια αξιολογήθηκαν' : 'Criteria evaluated'}: {Object.keys(formData.scores).length}/15</p>
                      <p>{language === 'el' ? 'Μέσος όρος' : 'Average'}: {
                        Object.values(formData.scores).length > 0 
                          ? (Object.values(formData.scores).reduce((a, b) => a + b, 0) / Object.values(formData.scores).length).toFixed(2)
                          : '-'
                      }</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="prev-step-btn"
          >
            <ArrowLeft size={18} className="mr-2" />
            {language === 'el' ? 'Προηγούμενο' : 'Previous'}
          </Button>
          
          {step < totalSteps - 1 ? (
            <Button 
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-[#E6007E] hover:bg-[#b8005f]"
              data-testid="next-step-btn"
            >
              {language === 'el' ? 'Επόμενο' : 'Next'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={submitting || Object.keys(formData.scores).length < minRequiredScores}
              className="bg-[#E6007E] hover:bg-[#b8005f]"
              data-testid="submit-evaluation-btn"
            >
              {submitting ? (
                language === 'el' ? 'Υποβολή...' : 'Submitting...'
              ) : (
                <>
                  <Check size={18} className="mr-2" />
                  {t('submitEvaluation')}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
